import { Button, PageHeader, Space, Tag, Typography } from 'antd'
import React from 'react'
import TableBlock from '../../components/tables/TableBlock'
import { getProductSizes } from '../../network/ProductSize'
import { ROLE_PERMISSIONS } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { createUrlWithFiltersFromKeyRequest } from '../../utils/filterUtils'
import useSearchParams from '../../hooks/useQuery'
import { formOptions } from '../../forms/blocks'

const { Link } = Typography

/** Product type filter options: same categories as FixedDesign */
const PRODUCT_TYPE_OPTIONS = Object.entries(formOptions)
  .filter(
    ([key]) =>
      !['collection', 'suit_2_pieces', 'suit_3_pieces', 'belt', 'sunglasses', 'default'].includes(key)
  )
  .map(([key, opt]) => ({ key, label: opt.name }))

const ProductSizeList = () => {
  const params = useSearchParams()
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role] ? ROLE_PERMISSIONS[user.role] : []

  const filters = (history) => ({
    fields: [
      {
        type: 'select',
        label: 'Product type',
        key: 'productType',
        default: '',
        options: PRODUCT_TYPE_OPTIONS,
      },
      { type: 'text', label: 'Name (EN)', key: 'name.en' },
      { type: 'text', label: 'Name (中文)', key: 'name.zh' },
      {
        type: 'select',
        label: 'Fixed design',
        key: 'isFixedDesign',
        default: '',
        options: [
          { key: 'true', label: 'Yes' },
          { key: 'false', label: 'No' },
        ],
      },
    ],
    actions: [
      {
        label: '+ Create product size preset',
        isActive:
          permissions.includes('fixed-designs') ||
          permissions.includes('fixed-sku') ||
          permissions.includes('product-sizes'),
        action: (keyRequest) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) currentPage = parseInt(allFilters._page, 10)
            } catch (e) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) currentPage = parseInt(pageParam, 10)
          }
          history.push(
            createUrlWithFiltersFromKeyRequest(
              '/product-sizes/create',
              keyRequest,
              currentPage
            )
          )
        },
      },
    ],
  })

  const columns = (history, keyRequest) => [
    {
      title: 'Product type',
      key: 'productType',
      dataIndex: 'productType',
      width: 160,
      sorter: true,
      render: (v) => (v && formOptions[v] ? formOptions[v].name : v) || '–',
    },
    {
      title: 'Name (EN)',
      key: 'name.en',
      dataIndex: ['name', 'en'],
      sorter: true,
      ellipsis: true,
      render: (v) => v ?? '–',
    },
    {
      title: 'Name (中文)',
      key: 'name.zh',
      dataIndex: ['name', 'zh'],
      sorter: true,
      ellipsis: true,
      render: (v) => v ?? '–',
    },
    {
      title: 'Fixed design',
      key: 'isFixedDesign',
      dataIndex: 'isFixedDesign',
      width: 120,
      sorter: true,
      render: (v) =>
        v ? (
          <Tag color="blue">Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      title: 'Fixed design ref',
      key: 'fixedDesignId',
      dataIndex: 'fixedDesignId',
      ellipsis: true,
      render: (v, record) => {
        const id = v?.id ?? (typeof v === 'string' ? v : null)
        if (!id) return '–'
        const label =
          v?.name?.en || v?.name?.zh || v?.sku || id
        return (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              history.push(`/fixed-designs/${id}`)
            }}
            style={{ cursor: 'pointer' }}
          >
            {label}
          </Link>
        )
      },
    },
    {
      title: '',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const pushUrl = (path) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) currentPage = parseInt(allFilters._page, 10)
            } catch (err) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) currentPage = parseInt(pageParam, 10)
          }
          history.push(
            createUrlWithFiltersFromKeyRequest(path, keyRequest, currentPage)
          )
        }
        return (
          <Space>
            <Button
              type="primary"
              onClick={() => pushUrl(`/product-sizes/${record.id}`)}
            >
              Edit
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <>
      <PageHeader className="site-page-header" title="Product size presets" />
      <TableBlock
        columns={columns}
        filters={filters}
        name="getProductSizes"
        request={getProductSizes}
      />
    </>
  )
}

export default ProductSizeList
